// /about: About Pixally.
//
// The entity page: who makes Pixally, what it is, and how it differs from the
// unrelated product with the same name. Built only from what the site already
// says in Ryan's own words (pages/AboutPage.jsx under /preview, and the founder
// section of PreLaunchPage.jsx) and from the legal pages (Pixally LLC, Delaware,
// Baltimore address). Nothing here is new about Ryan; if his story changes on
// those pages, it changes here.
//
// When the /preview marketing pages move to the root, /preview/about should
// redirect here rather than become a second About page.

import { PIXALLY_DEFINITION } from '../../lib/siteMeta.js';

export const ROUTE = '/about';
export const UPDATED_AT = '2026-10-02';
export const KICKER = 'About';
export const HERO_ART = 'reel';
export const PAGE_TYPE = 'AboutPage';

export const TITLE = 'About Pixally';
export const SEO_TITLE = 'About Pixally: the CRM for Wedding Photographers and Videographers';
export const SEO_DESCRIPTION =
  'Pixally is a CRM for wedding photographers and videographers, built by wedding filmmaker Ryan Geldermann and launching January 1, 2027.';

export const LEDE = PIXALLY_DEFINITION;

export const BODY = [
  { type: 'heading', level: 2, text: 'Built by a wedding filmmaker' },
  {
    type: 'paragraph',
    text: "Pixally was founded by Ryan Geldermann, who has been filming weddings for more than 15 years. He started solo in 2009, shooting everything himself and editing late into the night, and grew that work into Rain On Your Wedding Day, a full-service wedding photo and video company that now covers weddings across the country with a network of contractors.",
  },
  {
    type: 'paragraph',
    text: 'In 2020 he founded Uncut Gems, a wedding video editing company, and started seeing the same problems from the other side: hundreds of videographers managing second shooters in spreadsheets, tracking edits in their heads, and copying the same client details between half a dozen disconnected apps.',
  },
  {
    type: 'paragraph',
    text: 'In 2024, after trying the CRMs on the market and finding none that handled contractors, editing and delivery the way a wedding studio works, he started building Pixally: one place for leads, contracts, payments, the editing pipeline and client delivery.',
  },
  { type: 'heading', level: 2, text: 'Timeline' },
  {
    type: 'list',
    items: [
      '2009: Ryan starts shooting weddings, solo.',
      '2012: the first associate shooter. Spreadsheets and text threads begin.',
      '2015: Rain On Your Wedding Day launches as a full-service wedding photo and video company.',
      '2018: the company goes national, with a network of contractors.',
      '2020: Uncut Gems, a wedding video editing company, is founded.',
      '2024: Pixally begins.',
      'January 1, 2027: Pixally launches.',
    ],
  },
  { type: 'heading', level: 2, text: 'What Pixally is for' },
  { type: 'art', name: 'crew' },
  {
    type: 'paragraph',
    text: 'Pixally is for wedding photographers and videographers and the people they work with: second shooters, associates and editors. It is not trying to be a general tool for every kind of creative business. That focus is why it includes the things most CRMs leave to other apps: photo and film delivery, second shooter and contractor management, and post-production tracking.',
  },
  { type: 'heading', level: 2, text: 'The company' },
  {
    type: 'list',
    items: [
      'Pixally LLC, a Delaware limited liability company.',
      'Mailing address: 3600 Clipper Mill Rd, Ste 213, Baltimore, MD 21211, United States.',
      'Website: pixally.com. Support: support@pixally.com. Privacy: privacy@pixally.com.',
      'Pixally at pixally.com is not connected to an unrelated link-shortening product that has used the same name.',
    ],
  },
  { type: 'heading', level: 2, text: 'Learn more' },
  {
    type: 'list',
    items: [
      'Plans, fees, galleries and second shooters: [the Pixally FAQ](/faq)',
      'How Pixally compares with HoneyBook: [Pixally vs HoneyBook](/compare/honeybook)',
      'Field notes on running a wedding business: [the Pixally Field Notes](/blog)',
    ],
  },
];

export const FAQ = [
  {
    q: 'Who founded Pixally?',
    a: 'Ryan Geldermann, a wedding filmmaker who has been filming weddings since 2009, runs the wedding photo and video company Rain On Your Wedding Day, and founded the wedding video editing company Uncut Gems.',
  },
  {
    q: 'When was Pixally started?',
    a: 'Work on Pixally began in 2024. It launches on January 1, 2027.',
  },
  {
    q: 'Is Pixally the same as the Pixally link shortener?',
    a: 'No. Pixally at pixally.com is a CRM for wedding photographers and videographers, and has no connection to an unrelated link-shortening product with the same name.',
  },
];

export const SOURCES = [];
